import { useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { AccessSettings } from "../../../../../../models/access.settings";
import { Error } from "../../../../../../models/error";
import { Searches, Filters, Sorts, Sort } from "../../../../../../models/shipeezi.table";
import AccessSettingsService from "../../../../../../services/access.settings.service";
import { useAppState } from "../../../../../../context/StateContext";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";

const useActions = () => {
  const { currentUser } = useAppState();

  const [accesses, setAccesses] = useState<AccessSettings[]>([]);
  const [hasErrors, setHasErrors] = useState<boolean>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [sorting, setSorting] = useState<Sorts>();

  const { refetch, isFetching } = useQuery(
    ["access.settings.list", page, pageSize, filters, searches, sorting],
    () =>
      AccessSettingsService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        sortBy: sorting ? JSON.stringify([...sorting]) : JSON.stringify([["name", "asc"]]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setAccesses(response.data);
      },
    }
  );

  const { refetch: getAccessesCount } = useQuery(
    ["access.settings.count", searches, filters],
    () =>
      AccessSettingsService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
        else setTotal(0);
      },
    }
  );

  const { mutate: updateAccessSettings, isLoading: isUpdating } = useMutation(
    ["update.access.settings"],
    ({ id, access }: { id: number; access: AccessSettings }) =>
      AccessSettingsService.update(id, access),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        getAccessesCount();
      },
      onError: (error: AxiosError<Error>) => {
        NotificationToast.error(error?.response?.data?.message);
        setHasErrors(true);
      },
    }
  );

  const { mutate: createAccessSettings, isLoading: isCreating } = useMutation(
    ["create.access.settings"],
    (access: AccessSettings) => AccessSettingsService.create(access),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        getAccessesCount();
      },
      onError: (error: AxiosError<Error>) => {
        NotificationToast.error(error?.response?.data?.message);
        setHasErrors(true);
      },
    }
  );

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  return {
    accesses,
    page,
    pageSize,
    total,
    hasErrors,
    isLoading: isFetching || isCreating || isUpdating,
    setPageSize,
    setHasErrors,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    createAccessSettings,
    updateAccessSettings,
  };
};

export default useActions;
